import { create } from 'zustand';
import { getExistingSocket } from '../multiplayer/socket.ts';
import { C2S } from '../multiplayer/events.ts';

interface SettingsState {
  hideOtherScores: boolean;
  hidePathIndicators: boolean;
  soundVolume: number;
  colorblindMode: boolean;
  appearOnline: boolean;
  acceptChallenges: boolean;
  toggleHideOtherScores: () => void;
  toggleHidePathIndicators: () => void;
  setSoundVolume: (v: number) => void;
  toggleColorblindMode: () => void;
  toggleAppearOnline: () => void;
  toggleAcceptChallenges: () => void;
}

const initialColorblind = localStorage.getItem('rr-colorblind-mode') === 'true';
if (initialColorblind) document.body.classList.add('colorblind');

const useSettings = create<SettingsState>((set, get) => ({
  hideOtherScores: localStorage.getItem('rr-hide-scores') === 'true',
  hidePathIndicators: localStorage.getItem('rr-hide-paths') === 'true',
  appearOnline: localStorage.getItem('rr-appear-online') !== 'false',
  acceptChallenges: localStorage.getItem('rr-accept-challenges') !== 'false',
  soundVolume: (() => {
    const stored = localStorage.getItem('rr-sound-volume');
    if (stored === null) return 1;
    // migrate legacy mute flag
    if (stored === 'true') return 0;
    if (stored === 'false') return 1;
    const n = parseFloat(stored);
    return isNaN(n) ? 1 : Math.max(0, Math.min(1, n));
  })(),
  colorblindMode: initialColorblind,

  toggleHideOtherScores: () =>
    set((s) => {
      const next = !s.hideOtherScores;
      localStorage.setItem('rr-hide-scores', String(next));
      return { hideOtherScores: next };
    }),

  toggleHidePathIndicators: () =>
    set((s) => {
      const next = !s.hidePathIndicators;
      localStorage.setItem('rr-hide-paths', String(next));
      return { hidePathIndicators: next };
    }),

  setSoundVolume: (v: number) =>
    set(() => {
      const clamped = Math.max(0, Math.min(1, v));
      localStorage.setItem('rr-sound-volume', String(clamped));
      return { soundVolume: clamped };
    }),

  toggleColorblindMode: () => {
    const next = !get().colorblindMode;
    localStorage.setItem('rr-colorblind-mode', String(next));
    if (next) document.body.classList.add('colorblind');
    else document.body.classList.remove('colorblind');
    set({ colorblindMode: next });
  },

  toggleAppearOnline: () =>
    set((s) => {
      const next = !s.appearOnline;
      localStorage.setItem('rr-appear-online', String(next));
      getExistingSocket()?.emit(C2S.PRESENCE_SET_STATUS, { appearOnline: next });
      return { appearOnline: next };
    }),

  toggleAcceptChallenges: () =>
    set((s) => {
      const next = !s.acceptChallenges;
      localStorage.setItem('rr-accept-challenges', String(next));
      getExistingSocket()?.emit(C2S.PRESENCE_SET_STATUS, { acceptChallenges: next });
      return { acceptChallenges: next };
    }),
}));

export default useSettings;
